import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { TaskTypes } from '~/api/types';
import {
    emptyClientTask,
    getCurrentTime
} from '~/components/AddTaskModal/utils/addTaskModalUtils';
import type { RootState } from '~/store';
import type {
    AddOperationalStopState,
    TwoPartStopData,
    OperationalStopTask
} from './types';

export const defaultOperationalStopTask: OperationalStopTask = {
    ...emptyClientTask,
    date: new Date().toISOString(),
    deliveryServiceTimeMins: '25',
    pickupServiceTimeMins: '25',
    taskType: TaskTypes.PICKUP,
    deliveryTimeWindows: [
        {
            start: getCurrentTime({
                type: 'start',
                taskType: TaskTypes.DELIVERY
            }).toISOString(),
            end: getCurrentTime({
                type: 'end',
                taskType: TaskTypes.DELIVERY
            }).toISOString()
        }
    ],
    pickupTimeWindows: [
        {
            start: getCurrentTime({
                type: 'start',
                taskType: TaskTypes.PICKUP
            }).toISOString(),
            end: getCurrentTime({
                type: 'end',
                taskType: TaskTypes.PICKUP
            }).toISOString()
        }
    ]
};

export const initialState: AddOperationalStopState = {
    isOpenOperationalStopModal: false,
    operationalStopTask: defaultOperationalStopTask,
    twoPartStopData: null
};

export const addOperationalStopsSlice = createSlice({
    name: 'addOperationalStop',
    initialState,
    reducers: {
        setIsOpenOperationalStopModal: (
            state: AddOperationalStopState,
            action: PayloadAction<boolean>
        ) => {
            return {
                ...state,
                isOpenOperationalStopModal: action.payload
            };
        },
        setTwoPartStopData: (
            state: AddOperationalStopState,
            action: PayloadAction<TwoPartStopData>
        ) => {
            return {
                ...state,
                twoPartStopData: { ...action.payload }
            };
        },
        setExternalTaskType: (
            state: AddOperationalStopState,
            action: PayloadAction<string>
        ) => {
            return {
                ...state,
                operationalStopTask: {
                    ...state.operationalStopTask,
                    externalTaskType: action.payload
                }
            };
        },
        setPickupServiceTimeMins: (
            state: AddOperationalStopState,
            action: PayloadAction<string>
        ) => {
            return {
                ...state,
                operationalStopTask: {
                    ...state.operationalStopTask,
                    pickupServiceTimeMins: action.payload
                }
            };
        },
        setPickupAddress: (
            state: AddOperationalStopState,
            action: PayloadAction<Record<string, string | undefined>>
        ) => {
            return {
                ...state,
                operationalStopTask: {
                    ...state.operationalStopTask,
                    ...action.payload
                }
            };
        },
        resetOperationalStopTask: (state: AddOperationalStopState) => {
            return {
                ...state,
                operationalStopTask: defaultOperationalStopTask
            };
        }
    }
});

export const {
    setIsOpenOperationalStopModal,
    setExternalTaskType,
    setPickupServiceTimeMins,
    resetOperationalStopTask,
    setPickupAddress,
    setTwoPartStopData
} = addOperationalStopsSlice.actions;

export const selectIsOpenOperationalStopModal = (state: RootState): boolean =>
    state.addOperationalStop.isOpenOperationalStopModal;

export const selectOperationalStop = (
    state: RootState
): typeof defaultOperationalStopTask =>
    state.addOperationalStop.operationalStopTask;

export const selectTwoPartStopData = (state: RootState) =>
    state.addOperationalStop.twoPartStopData;

export default addOperationalStopsSlice.reducer;
